import { useState } from "react";
import * as XLSX from "xlsx";
import usePortfolioExposure from "../hooks/usePortfolioExposure";
import { fmtMoney } from "../parsers/xmlParser";
import { useIsMobile } from "../hooks/useIsMobile";

const METRICS = [
  { key: "stock", label: "חשיפה למניות", color: "#4338ca" },
  { key: "foreign", label: 'חשיפה לחו"ל', color: "#0891b2" },
  { key: "forex", label: 'חשיפה למט"ח', color: "#059669" },
  { key: "liquid", label: "נכסים נזילים", color: "#d97706" },
];

const fmtPct = (v) => v != null ? `${v.toFixed(1)}%` : "—";

function exportToExcel(details, totals) {
  const rows = details.map(d => ({
    "מסלול": d.name,
    "חברה": d.fundName,
    "צבירה": d.balance,
    "משקל": d.weight / 100,
    "חשיפה למניות": d.stock != null ? d.stock / 100 : null,
    'חשיפה לחו"ל': d.foreign != null ? d.foreign / 100 : null,
    'חשיפה למט"ח': d.forex != null ? d.forex / 100 : null,
    "נכסים נזילים": d.liquid != null ? d.liquid / 100 : null,
  }));
  rows.push({
    "מסלול": "ממוצע משוקלל",
    "חברה": "",
    "צבירה": details.reduce((s, d) => s + d.balance, 0),
    "משקל": 1,
    "חשיפה למניות": totals.stock != null ? totals.stock / 100 : null,
    'חשיפה לחו"ל': totals.foreign != null ? totals.foreign / 100 : null,
    'חשיפה למט"ח': totals.forex != null ? totals.forex / 100 : null,
    "נכסים נזילים": totals.liquid != null ? totals.liquid / 100 : null,
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  const colCount = Object.keys(rows[0]).length;
  for (let r = 0; r <= rows.length; r++) {
    for (let c = 3; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: r + 1, c });
      if (ws[addr] && ws[addr].v != null) ws[addr].z = "0.0%";
    }
    const moneyAddr = XLSX.utils.encode_cell({ r: r + 1, c: 2 });
    if (ws[moneyAddr] && ws[moneyAddr].v != null) ws[moneyAddr].z = '#,##0';
  }
  ws["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "חשיפת תיק");
  XLSX.writeFile(wb, "portfolio_exposure.xlsx");
}

function DetailModal({ details, totals, unmatched, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} dir="rtl" style={{
        background: "white", borderRadius: 16, padding: "20px 24px", maxWidth: 900,
        width: "90vw", maxHeight: "80vh", overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1e293b" }}>פירוט חשיפת תיק לפי מסלול</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => exportToExcel(details, totals)} style={{
              background: "#16a34a", color: "white", border: "none", borderRadius: 8,
              padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
            }}>Excel ייצוא</button>
            <button onClick={onClose} style={{
              background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", padding: "4px 8px",
            }}>✕</button>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              <th style={thStyle}>מסלול</th>
              <th style={thStyle}>חברה</th>
              <th style={{ ...thStyle, textAlign: "left" }}>צבירה</th>
              <th style={{ ...thStyle, textAlign: "left" }}>משקל</th>
              {METRICS.map(m => (
                <th key={m.key} style={{ ...thStyle, textAlign: "left", color: m.color }}>{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {details.map((d, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={tdStyle}>{d.name}</td>
                <td style={{ ...tdStyle, color: "#64748b" }}>{d.fundName}</td>
                <td style={{ ...tdStyle, textAlign: "left", fontWeight: 600 }}>{fmtMoney(d.balance)}</td>
                <td style={{ ...tdStyle, textAlign: "left", fontWeight: 600 }}>{d.weight.toFixed(1)}%</td>
                {METRICS.map(m => (
                  <td key={m.key} style={{ ...tdStyle, textAlign: "left" }}>{fmtPct(d[m.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f0f9ff", borderTop: "2px solid #3b82f6", fontWeight: 800 }}>
              <td style={tdStyle} colSpan={3}>ממוצע משוקלל</td>
              <td style={tdStyle} />
              {METRICS.map(m => (
                <td key={m.key} style={{ ...tdStyle, textAlign: "left", color: m.color }}>{fmtPct(totals[m.key])}</td>
              ))}
            </tr>
          </tfoot>
        </table>

        {unmatched?.length > 0 && (
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
              מסלולים ללא נתוני API ({unmatched.length}):
            </div>
            {unmatched.map((u, i) => (
              <div key={i} style={{ fontSize: 11, color: "#7f1d1d" }}>
                {u.fundName} — {u.name} ({fmtMoney(u.balance)})
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = { padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#475569" };
const tdStyle = { padding: "7px 10px", textAlign: "right" };

export default function PortfolioExposure({ funds }) {
  const [showDetail, setShowDetail] = useState(false);
  const { loading, stock, foreign, forex, liquid, matched, total, details, unmatched } = usePortfolioExposure(funds);
  const isMobile = useIsMobile();

  if (loading || matched === 0) return null;

  const values = { stock, foreign, forex, liquid };

  const pillStyle = {
    fontSize: 10, fontWeight: 700, color: "#4338ca",
    background: "#4338ca14", border: "1px solid #4338ca55",
    borderRadius: 6, padding: "1px 6px", lineHeight: 1.5,
    flexShrink: 0,
  };

  return (
    <>
      <div onClick={() => setShowDetail(true)} style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 8 : 12,
        marginBottom: 16,
        background: "white", borderRadius: 14, padding: "10px 16px",
        border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        cursor: "pointer", transition: "border-color 0.15s",
        overflow: "hidden",
      }}>
        {/* כותרת + פירוט */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>
            חשיפת תיק משוקללת
          </div>
          {isMobile && (
            <span style={pillStyle} title="לחץ לפירוט מלא">פירוט ›</span>
          )}
        </div>

        {/* מדדים */}
        <div style={{
          display: isMobile ? "grid" : "flex",
          gridTemplateColumns: isMobile ? "1fr 1fr" : undefined,
          gap: isMobile ? "8px 12px" : 16,
          alignItems: "center",
          width: isMobile ? "100%" : undefined,
        }}>
          {METRICS.map(({ key, label, color }) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#64748b" }}>{label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                {fmtPct(values[key])}
              </span>
            </div>
          ))}
          {!isMobile && (
            <span style={pillStyle} title="לחץ לפירוט מלא">פירוט ›</span>
          )}
        </div>

        {matched < total && (
          <div style={{ fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap" }}>
            {matched} מתוך {total} מסלולים
          </div>
        )}
      </div>

      {showDetail && details?.length > 0 && (
        <DetailModal details={details} totals={values} unmatched={unmatched} onClose={() => setShowDetail(false)} />
      )}
    </>
  );
}
